import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileUp, Loader2, Sparkles, FileText, X } from "lucide-react";
import { sizeKo } from "./studioUtils";

const MAX = 15 * 1024 * 1024;

/** Dépôt d'un cahier des charges PDF → lance l'analyse IA. `moduleId` = nouvelle version d'un module existant. */
export function UploadDialog({ open, onOpenChange, moduleId, moduleName, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const pick = (f) => {
    if (!f) return;
    if (!(f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"))) return toast.error("Déposez un fichier PDF");
    if (f.size > MAX) return toast.error("PDF trop volumineux (15 Mo max)");
    setFile(f);
  };
  const reset = () => { setFile(null); setName(""); setInstructions(""); };
  const submit = async () => {
    if (!file) return toast.error("Choisissez un PDF");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim());
    fd.append("instructions", instructions.trim());
    if (moduleId) fd.append("module_id", moduleId);
    try {
      const { data } = await api.post("/admin/studio/modules", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(moduleId ? "Nouvelle version : analyse IA lancée" : "PDF déposé : l'IA analyse votre cahier des charges");
      reset(); onOpenChange(false); onDone?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Dépôt impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-xl" data-testid="studio-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black">{moduleId ? `Nouvelle version — ${moduleName || "module"}` : "Nouveau module à partir d'un PDF"}</DialogTitle>
          <DialogDescription className="text-xs">{moduleId ? "Le PDF est analysé en tenant compte du plan actuel : les clés existantes sont conservées, la version en ligne reste disponible jusqu'à la nouvelle publication." : "Cahier des charges, procédure, formulaire papier scanné… L'IA lit le document (texte et images) et construit un module complet : fiches, champs, statuts, vues, indicateurs, rappels."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
            className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-[#002FA7] bg-[#EFF6FF]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`} data-testid="studio-dropzone">
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="studio-file-input" />
            {file ? (
              <div className="flex items-center justify-center gap-3 text-sm" data-testid="studio-file-selected"><FileText className="h-5 w-5 text-[#002FA7]" /><span className="font-bold truncate max-w-[280px]">{file.name}</span><span className="text-xs text-[#868E96]">{sizeKo(file.size)}</span><button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="h-6 w-6 flex items-center justify-center hover:bg-[#F1F3F5]" aria-label="Retirer"><X className="h-3.5 w-3.5" /></button></div>
            ) : (
              <><FileUp className="h-7 w-7 mx-auto text-[#868E96]" /><div className="mt-2 text-sm font-bold">Glissez votre PDF ici ou cliquez pour choisir</div><div className="text-xs text-[#868E96] mt-1">PDF texte ou scanné · 15 Mo max</div></>
            )}
          </div>
          {!moduleId && (
            <label className="block space-y-1"><span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057]">Nom du module (optionnel)</span><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Laissez vide : l'IA le déduit du document" className="h-10" data-testid="studio-upload-name" /></label>
          )}
          <label className="block space-y-1"><span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057]">Consignes pour l'IA (optionnel)</span><Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} maxLength={3000} placeholder="Ex. : les salariés ne doivent voir que leurs propres fiches ; ajouter un rappel 7 jours avant l'échéance ; tarif option 12 €/mois…" data-testid="studio-upload-instructions" /></label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="h-10" data-testid="studio-upload-cancel">Annuler</Button>
            <Button onClick={submit} disabled={busy || !file} className="h-10 bg-[#002FA7] hover:bg-[#00247F] text-white font-bold" data-testid="studio-upload-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}{busy ? "Envoi…" : "Lancer l'analyse IA"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
